import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

type LogoMap = Record<string, string>;

let cache: LogoMap | null = null;
let inflight: Promise<LogoMap> | null = null;

const normalize = (name: string) => name.trim().toLowerCase();

async function loadLogos(): Promise<LogoMap> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    const { data, error } = await supabase.from("team_logos").select("team_name, logo_url");
    const map: LogoMap = {};
    if (!error) {
      for (const row of (data as { team_name: string; logo_url: string }[]) ?? []) {
        map[normalize(row.team_name)] = row.logo_url;
      }
      cache = map;
    }
    inflight = null;
    return map;
  })();
  return inflight;
}

const TeamLogosContext = createContext<{ enabled: boolean; logos: LogoMap }>({
  enabled: false,
  logos: {},
});

/** Provides club crests to TeamFlag when the league has them enabled. */
export const TeamLogosProvider = ({ enabled, children }: { enabled: boolean; children: ReactNode }) => {
  const [logos, setLogos] = useState<LogoMap>(cache ?? {});

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    loadLogos().then((m) => {
      if (alive) setLogos(m);
    });
    return () => {
      alive = false;
    };
  }, [enabled]);

  return <TeamLogosContext.Provider value={{ enabled, logos }}>{children}</TeamLogosContext.Provider>;
};

/** Crest URL for a team, or null when logos are off / unknown team. */
export const useTeamLogo = (team: string | null | undefined): string | null => {
  const { enabled, logos } = useContext(TeamLogosContext);
  if (!enabled || !team) return null;
  return logos[normalize(team)] ?? null;
};
